import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useB2BAuthStore } from './stores/useB2BAuthStore';
import StartScreen from './screens/StartScreen';
import AuthScreen from './screens/AuthScreen';
import PricingScreen from './screens/PricingScreen';
import PaymentResultScreen from './screens/PaymentResultScreen';
import SearchScreen from './screens/SearchScreen';
import ResultsScreen from './screens/ResultsScreen';
import JobDetailsScreen from './screens/JobDetailsScreen';
import VacancyDetailsScreen from './screens/VacancyDetailsScreen';
import ChatScreen from './screens/ChatScreen';
import ChatSettingScreen from './screens/ChatSettingScreen';
import SavedJobsScreen from './screens/SavedJobsScreen';
import ChatsListScreen from './screens/ChatsListScreen';
import ProfileScreen from './screens/ProfileScreen';
import CancelSubscriptionScreen from './screens/CancelSubscriptionScreen';
import InterviewScreen from './screens/InterviewScreen';
import GoogleCallbackScreen from './screens/GoogleCallbackScreen';
import ScreeningScreen from './screens/ScreeningScreen';
import QuizScreen from './screens/QuizScreen';
import RequireB2BAuth from './components/b2b/RequireB2BAuth';
import B2BDashboardScreen from './screens/b2b/DashboardScreen';
import B2BJobsListScreen from './screens/b2b/JobsListScreen';
import B2BJobConstructorScreen from './screens/b2b/JobConstructorScreen';
import B2BJobDetailScreen from './screens/b2b/JobDetailScreen';
import B2BCompaniesScreen from './screens/b2b/CompaniesScreen';
import B2BCompanyDetailScreen from './screens/b2b/CompanyDetailScreen';
import B2BCandidatesScreen from './screens/b2b/CandidatesScreen';
import B2BAddCandidateScreen from './screens/b2b/AddCandidateScreen';
import B2BCandidateDetailScreen from './screens/b2b/CandidateDetailScreen';
import B2BScreeningEditorScreen from './screens/b2b/ScreeningEditorScreen';
import B2BPricingScreen from './screens/b2b/PricingScreen';
import B2BBillingScreen from './screens/b2b/BillingScreen';
import B2BPaymentResultScreen from './screens/b2b/B2BPaymentResultScreen';
import B2BQuizzesListScreen from './screens/b2b/QuizzesListScreen';
import B2BQuizReportScreen from './screens/b2b/QuizReportScreen';
import ForRecruitersLandingScreen from './screens/ForRecruitersLandingScreen';
import B2BTermsScreen from './screens/b2b/B2BTermsScreen';
import DPAScreen from './screens/b2b/DPAScreen';
import B2BComplianceScreen from './screens/b2b/ComplianceScreen';
import B2BSettingsScreen from './screens/b2b/SettingsScreen';
import B2BMembersScreen from './screens/b2b/MembersScreen';
import B2BInviteAcceptScreen from './screens/b2b/InviteAcceptScreen';
import BlogScreen from './screens/BlogScreen';
import BlogArticleScreen from './screens/BlogArticleScreen';
import TermsScreen from './screens/TermsScreen';
import RefundPolicyScreen from './screens/RefundPolicyScreen';
import ContactsScreen from './screens/ContactsScreen';
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen';
import CookiePolicyScreen from './screens/CookiePolicyScreen';
import AINoticeScreen from './screens/AINoticeScreen';
import SubProcessorsScreen from './screens/SubProcessorsScreen';
import AcceptableUseScreen from './screens/AcceptableUseScreen';
import CCPARightsScreen from './screens/CCPARightsScreen';
import CandidatePrivacyScreen from './screens/CandidatePrivacyScreen';
import HowItWorksScreen from './screens/HowItWorksScreen';
import CareerLandingScreen from './screens/CareerLandingScreen';
import PillarScreen from './screens/PillarScreen';
import SeoLandingScreen from './screens/SeoLandingScreen';
import ProtectedRoute from './components/ProtectedRoute';
import SeoManager from './components/SeoManager';
import FeedbackWidget from './components/FeedbackWidget';
import CookieBanner from './components/CookieBanner';
import { clearAuthSession, isTokenExpired } from './utils/auth';
import './App.css'

const protectedPrefixes = ['/profile', '/interview', '/chat', '/chat-setting', '/saved', '/chats', '/vacancy'];
const AUTH_TOKEN_KEY = 'authToken';

function B2BIndexRedirect() {
  const isAuthenticated = useB2BAuthStore((s) => s.isAuthenticated);
  return <Navigate to={isAuthenticated ? '/b2b/dashboard' : '/for-recruiters'} replace />;
}

function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const isSeoBot =
    typeof navigator !== 'undefined' && /googlebot|bingbot/i.test(navigator.userAgent || '');
  const isBotBlockedPath =
    location.pathname.startsWith('/profile') ||
    location.pathname.startsWith('/interview') ||
    location.pathname.startsWith('/chat') ||
    location.pathname.startsWith('/chats') ||
    // /b2b/* is a private recruiter app, /screen/* is a per-candidate screening URL,
    // /quiz/* is a public lead-gen quiz — none should be indexed.
    (location.pathname.startsWith('/b2b/') && !location.pathname.startsWith('/b2b/auth')) ||
    location.pathname.startsWith('/screen/') ||
    location.pathname.startsWith('/quiz/');

  useEffect(() => {
    const token =
      (() => {
        try {
          return localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY);
        } catch {
          return null;
        }
      })();
    if (!token) return;
    if (!isTokenExpired(token)) return;
    clearAuthSession();
    const isProtected = protectedPrefixes.some((prefix) => location.pathname.startsWith(prefix));
    if (isProtected) {
      navigate('/', {
        replace: true,
        state: {
          openAuth: true,
          action: location.pathname.startsWith('/profile') || location.pathname.startsWith('/interview') ? 'profile' : 'search',
          redirectTo: `${location.pathname}${location.search}${location.hash}`,
        },
      });
    }
  }, [location, navigate]);

  if (isSeoBot && isBotBlockedPath) {
    return (
      <>
        <SeoManager />
        <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B] flex items-center justify-center px-6">
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-xl">
            <h1 className="text-2xl font-semibold">Access denied</h1>
            <p className="text-gray-500 mt-3">
              This page is unavailable for search crawler user agents.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SeoManager />
      <FeedbackWidget />
      <Routes>
        <Route path="/" element={<StartScreen />} />
        <Route path="/how-it-works" element={<HowItWorksScreen />} />
        <Route path="/pricing" element={<PricingScreen />} />
        <Route path="/blog" element={<BlogScreen />} />
        <Route path="/blog/:slug" element={<BlogArticleScreen />} />
        <Route path="/terms" element={<TermsScreen />} />
        <Route path="/refund-policy" element={<RefundPolicyScreen />} />
        <Route path="/contacts" element={<ContactsScreen />} />
        <Route path="/privacy" element={<PrivacyPolicyScreen />} />
        <Route path="/cookies" element={<CookiePolicyScreen />} />
        <Route path="/ai" element={<AINoticeScreen />} />
        <Route path="/privacy/sub-processors" element={<SubProcessorsScreen />} />
        <Route path="/aup" element={<AcceptableUseScreen />} />
        <Route path="/ca-privacy-rights" element={<CCPARightsScreen />} />
        <Route path="/screen/privacy" element={<CandidatePrivacyScreen />} />
        <Route path="/payments/result" element={<PaymentResultScreen />} />
        <Route path="/login" element={<AuthScreen />} />
        <Route path="/register" element={<AuthScreen />} />
        <Route path="/b2b/auth" element={<AuthScreen />} />
        <Route path="/screen/:token" element={<ScreeningScreen />} />
        <Route path="/quiz/:slug" element={<QuizScreen />} />

        <Route path="/for-recruiters" element={<ForRecruitersLandingScreen />} />

        {/* B2B legal pages — public, no auth required */}
        <Route path="/b2b/terms" element={<B2BTermsScreen />} />
        <Route path="/b2b/dpa" element={<DPAScreen />} />
        <Route path="/b2b/compliance" element={<B2BComplianceScreen />} />

        {/* B2B recruiter workspace */}
        {/* /b2b is the public marketing landing (auth-gated routes are under /b2b/dashboard etc.). */}
        <Route path="/b2b" element={<B2BIndexRedirect />} />
        <Route path="/b2b/dashboard" element={<RequireB2BAuth><B2BDashboardScreen /></RequireB2BAuth>} />
        <Route path="/b2b/jobs" element={<RequireB2BAuth><B2BJobsListScreen /></RequireB2BAuth>} />
        <Route path="/b2b/jobs/new" element={<RequireB2BAuth><B2BJobConstructorScreen /></RequireB2BAuth>} />
        <Route path="/b2b/jobs/:jobId" element={<RequireB2BAuth><B2BJobDetailScreen /></RequireB2BAuth>} />
        <Route path="/b2b/jobs/:jobId/edit" element={<RequireB2BAuth><B2BJobConstructorScreen /></RequireB2BAuth>} />
        <Route path="/b2b/jobs/:jobId/candidates/new" element={<RequireB2BAuth><B2BAddCandidateScreen /></RequireB2BAuth>} />
        <Route path="/b2b/jobs/:jobId/candidates/:candidateId" element={<RequireB2BAuth><B2BCandidateDetailScreen /></RequireB2BAuth>} />
        <Route path="/b2b/jobs/:jobId/candidates/:candidateId/screening" element={<RequireB2BAuth><B2BScreeningEditorScreen /></RequireB2BAuth>} />
        <Route path="/b2b/companies" element={<RequireB2BAuth><B2BCompaniesScreen /></RequireB2BAuth>} />
        <Route path="/b2b/companies/:companyId" element={<RequireB2BAuth><B2BCompanyDetailScreen /></RequireB2BAuth>} />
        <Route path="/b2b/candidates" element={<RequireB2BAuth><B2BCandidatesScreen /></RequireB2BAuth>} />
        <Route path="/b2b/pricing" element={<RequireB2BAuth><B2BPricingScreen /></RequireB2BAuth>} />
        <Route path="/b2b/billing" element={<RequireB2BAuth><B2BBillingScreen /></RequireB2BAuth>} />
        <Route path="/b2b/payment-result" element={<RequireB2BAuth><B2BPaymentResultScreen /></RequireB2BAuth>} />
        <Route path="/b2b/settings/workspace" element={<RequireB2BAuth><B2BSettingsScreen /></RequireB2BAuth>} />
        <Route path="/b2b/settings/members" element={<RequireB2BAuth><B2BMembersScreen /></RequireB2BAuth>} />
        <Route path="/b2b/quizzes" element={<RequireB2BAuth><B2BQuizzesListScreen /></RequireB2BAuth>} />
        <Route path="/b2b/quizzes/:slug" element={<RequireB2BAuth><B2BQuizReportScreen /></RequireB2BAuth>} />
        <Route path="/b2b/invite/:token" element={<B2BInviteAcceptScreen />} />
        <Route path="/auth/google/callback" element={<GoogleCallbackScreen />} />
        <Route path="/search" element={<SearchScreen />} />
        <Route path="/results" element={<ResultsScreen />} />
        <Route path="/career/:careerSlug" element={<CareerLandingScreen />} />
        <Route path="/role/:careerSlug" element={<CareerLandingScreen />} />
        <Route path="/interview-prep" element={<PillarScreen />} />
        <Route path="/interview-anxiety" element={<PillarScreen />} />
        <Route path="/ai-interview-prep" element={<PillarScreen />} />
        <Route path="/mock-interview-practice" element={<SeoLandingScreen />} />
        <Route path="/ai-mock-interview" element={<SeoLandingScreen />} />
        <Route path="/details/:jobId" element={<JobDetailsScreen />} />
        <Route
          path="/vacancy/:vacancyId"
          element={
            <ProtectedRoute action="search">
              <VacancyDetailsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat-setting"
          element={
            <ProtectedRoute action="search">
              <ChatSettingScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat-setting/:jobId"
          element={
            <ProtectedRoute action="search">
              <ChatSettingScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:jobId"
          element={
            <ProtectedRoute action="search">
              <ChatScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/saved"
          element={
            <ProtectedRoute action="search">
              <SavedJobsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chats"
          element={
            <ProtectedRoute action="search">
              <ChatsListScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute action="profile">
              <ProfileScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/cancel-subscription"
          element={
            <ProtectedRoute action="profile">
              <CancelSubscriptionScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/interview"
          element={
            <ProtectedRoute action="profile">
              <InterviewScreen />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-[#F0F0F0]">
        <AppRoutes />
        <CookieBanner />
      </div>
    </Router>
  )
}

export default App
